import { gql, useQuery } from '@apollo/client';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import useMedia from '../hooks/useMedia';
import { Button } from '../styles/theme/default/buttons';
import { SubtitleOne } from '../styles/theme/default/typography';

const Wrapper = styled.div`
  margin: auto;
  max-width: 1300px;
  padding: 0 100px;

  .ig-images-container {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
    grid-gap: 5px;
  }

  .follow-us-txt {
    text-align: center;
    margin-bottom: 24px;
    a {
      text-decoration: underline;
      color: ${({ theme }) => theme.colors.french};
    }
  }

  .button-container {
    display: flex;
    justify-content: center;
    margin-top: 24px;

    button {
      width: 216px;
    }
  }

  @media (max-width: 1300px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }

  @media (max-width: 1100px) {
    .ig-images-container {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }

  @media (max-width: 992px) {
    .button-container {
      margin-top: 16px;
      button {
        width: 100%;
      }
    }

    padding: 0;
  }
`;

function Images() {
  const isSmallDesktop =
    typeof window !== `undefined` ? useMedia('(max-width: 1300px) ') : null;

  const isTablet =
    typeof window !== `undefined` ? useMedia('(max-width: 1100px) ') : null;

  const defaultPageSize = isTablet ? 6 : isSmallDesktop ? 8 : 10;

  const { loading, data, error, fetchMore } = useQuery(INSTAGRAM_POSTS, {
    notifyOnNetworkStatusChange: true,
    variables: {
      first: defaultPageSize,
      page: 1
    }
  });

  if (error)
    return `Sorry there was an error, please try again or contact us ${error}`;

  const onLoadMore = () => {
    fetchMore({
      variables: {
        page: data.instagramPosts.paginatorInfo.currentPage + 1,
        first: defaultPageSize
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        let data = {};
        data.instagramPosts = Object.assign({}, prev.instagramPosts, {
          data: [
            ...prev.instagramPosts.data,
            ...fetchMoreResult.instagramPosts.data
          ],
          paginatorInfo: {
            ...fetchMoreResult.instagramPosts.paginatorInfo
          }
        });

        return data;
      }
    });
  };

  const renderInstagramPosts = data => {
    return data.instagramPosts.data.map(post => {
      return (
        <div key={post.caption}>
          <ImageContainer className="image-container">
            <a
              href="https://www.instagram.com/theinside/"
              target="_blank"
              rel="noopener"
            >
              {post.media_url && (
                <img
                  loading="lazy"
                  className="ig-post-image"
                  src={post.media_url}
                  alt={post.caption}
                />
              )}
            </a>
          </ImageContainer>
        </div>
      );
    });
  };

  const renderInstagramSkeleton = () => {
    return Array.from(Array(defaultPageSize).keys()).map((x, i) => {
      return (
        <div key={i}>
          <ImageContainer className="image-container">
            <a href="https://www.instagram.com/theinside/" target="_blank">
              <Skeleton />
            </a>
          </ImageContainer>
        </div>
      );
    });
  };

  return (
    <Wrapper>
      <div className="container">
        <SubtitleOne className="follow-us-txt" bold={true}>
          FOLLOW US ON INSTAGRAM{' '}
          <a
            rel="noopener"
            href="https://www.instagram.com/theinside/"
            className="ig-link"
            target="_blank"
          >
            @THEINSIDE
          </a>
        </SubtitleOne>
        <div className="ig-images-container">
          {loading && !data
            ? renderInstagramSkeleton()
            : renderInstagramPosts(data)}
        </div>
        {data?.instagramPosts?.paginatorInfo?.hasMorePages && (
          <div className="button-container">
            <Button onClick={onLoadMore} disabled={loading}>
              {loading ? (
                <div
                  style={{
                    borderWidth: '2px',
                    width: 15,
                    height: 15
                  }}
                  className="spinner-border text-dark"
                  role="status"
                >
                  <span className="sr-only">Loading...</span>
                </div>
              ) : (
                'Load More'
              )}
            </Button>
          </div>
        )}
      </div>
    </Wrapper>
  );
}

export default Images;

const ImageContainer = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding-bottom: 100%;
  a {
    position: absolute !important;
    top: 0;
    left: 0;
    background: #f7f9fa;
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    .ig-post-image {
      position: absolute;
      max-width: 100%;
      max-height: 100%;
      position: absolute;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`;

const INSTAGRAM_POSTS = gql`
  query instagramPosts($first: Int!, $page: Int!) {
    instagramPosts(first: $first, page: $page) {
      paginatorInfo {
        count
        currentPage
        firstItem
        hasMorePages
        lastItem
        lastPage
        perPage
        total
      }
      data {
        id
        caption
        media_url
      }
    }
  }
`;
